import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { IoniconsOutline, ND } from '@pt/pt-models';
import { WidgetAppSummaryV0PreviewDto } from './models/widget-app-summary-v0-preview.dto';
import { WidgetAppSummaryV0ResponseValue } from './models/widget-app-summary-v0-response-value';
import { EChartsOption } from 'echarts';
import { of, Subject } from 'rxjs';
import { WidgetDialogService } from '../../../services/widget-dialog.service';
import { WidgetAppSummaryV0RidMappingDto } from './models/widget-app-summary-v0-rid-mapping.dto';
import { AppAssessmentLevel } from 'it/primatoo/openapi/api/org/schemas/appassessment/AppAssessmentLevel';
import { WidgetAppSummaryV0QueryResponseDto } from './models/widget-app-summary-v0-query-response.dto';
import {
  AppSummaryConfiguration,
  AppSummaryRowChart,
  AppSummaryRowV2,
  RID_GAUGE_PALETTE,
} from './models/app-summary-models';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { WidgetAppSummaryV0Component } from './widget-app-summary-v0.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'pt-widget-app-summary-v0-preview',
  template: `
    <ng-container
      *ngIf="loading || !data; then LOADING; else PREVIEW"
    ></ng-container>

    <ng-template #LOADING>Loading ...</ng-template>

    <ng-template #PREVIEW>
      <ng-container *ngIf="noDataV2; then NODATA; else DATA"></ng-container>

      <ng-template #NODATA>
        <pt-widget-nodata [noDataLabel]="noDataLabel"></pt-widget-nodata>
      </ng-template>

      <ng-template #DATA>
        <div class="d-flex flex-column">
          <table class="table table-striped">
            <thead>
              <tr>
                <th scope="col"></th>
                <th scope="col"></th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of rowDataV2">
                <td class="status-col" [ngClass]="'bg-' + row.status">
                  <div class="text-vertical" *ngIf="row.status !== 'EQUAL'">
                    {{ 'ENUMS.' + row.status | translate | uppercase }}
                  </div>
                </td>

                <td>
                  <pt-widget-app-summary-row-app-v0
                    [appId]="row.appId"
                    [appName]="row.appName"
                    [requiredAlign]="row.showAlignBtn"
                    (alignValue)="alignValue.emit(row.appUUID)"
                  >
                  </pt-widget-app-summary-row-app-v0>
                </td>

                <td>
                  <pt-widget-app-summary-row-rid-v2-v0
                    [editable]="editable"
                    [row]="row"
                    (alignValue)="alignValue.emit(row.appUUID)"
                  >
                  </pt-widget-app-summary-row-rid-v2-v0>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="wasp-note-title">
          {{ data?.header?.note }}
          <pt-icon
            *ngIf="editable"
            [key]="IconEdit"
            class="d-flex cursor-pointer ms-1"
            (click)="openTextArea(currentNote, data?.header?.note)"
            [size]="12"
          ></pt-icon>
        </div>
        <div class="wasp-note" [innerHTML]="currentNoteHtml"></div>
      </ng-template>
    </ng-template>
  `,
  styleUrls: [`widget-app-summary-preview-v0.component.scss`],
})
export class WidgetAppSummaryPreviewV0Component
  implements OnChanges, OnDestroy, OnInit
{
  @Input() noDataLabel?: string;
  @Input() data: WidgetAppSummaryV0PreviewDto;
  @Input() warningLabel: string;
  @Input() loading: boolean;
  @Input() editable = false;
  @Input() ridMapping: WidgetAppSummaryV0RidMappingDto[] = [];
  @Output() noteChangeValue = new EventEmitter<string>();
  @Output() alignValue = new EventEmitter<string>();
  rowDataV2: AppSummaryRowV2[] = [];
  noDataV2 = true;
  IconEdit = IoniconsOutline.edit;
  ND = ND;
  currentNote: string;
  currentNoteHtml: string;

  // --- Proprietà da aggiungere alla classe ---
  private populateRowData$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  constructor(
    private widgetDialogService: WidgetDialogService,
    private translateService: TranslateService
  ) {}
  ngOnInit(): void {
    this.populateRowData$
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe(() => {
        this.populateRowData();
        this.currentNote = this.data?.note;
        this.currentNoteHtml = this.currentNote
          ? this.currentNote.split('\n').join('<br>')
          : '';
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data) {
      this.populateRowData$.next();
    }
  }

  // --- In ngOnDestroy() ---
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openTextArea(note: string, title: string) {
    const dialog = this.widgetDialogService.runTextArea(of(title), note, true);
    dialog.subscribe((value) => {
      this.currentNote = value;
      this.noteChangeValue.emit(this.currentNote);
    });
  }

  private populateRowData(): void {
    const responseV2: AppSummaryRowV2[] = [];

    if (this.data) {
      // const dataQueryValues = FAKE_QUERY_VALUE; //this.data.queryValues;
      // const dataWidgetValue = FAKE_WIDGET_VALUE; //this.data.widgetValue;
      const dataQueryValues = this.data.queryValues;
      const dataWidgetValue = this.data.widgetValue;

      dataQueryValues.forEach((app) => {
        const widgetValue: WidgetAppSummaryV0ResponseValue | undefined =
          dataWidgetValue.find((r) => r.id === app.id);
        const appRidValue: AppSummaryRowChart[] | undefined =
          app.scenarioRImplValue ||
          app.scenarioIImplValue ||
          app.scenarioDImplValue
            ? [
                {
                  label: of(this.getLevelLabel(app.scenarioRLevel)),
                  description: of(this.data.header.scenarioR),
                  value: app.scenarioRImplValue,
                  options: this.generateChartPieOption(app.scenarioRImplValue),
                },
                {
                  label: of(this.getLevelLabel(app.scenarioILevel)),
                  description: of(this.data.header.scenarioI),
                  value: app.scenarioIImplValue,
                  options: this.generateChartPieOption(app.scenarioIImplValue),
                },
                {
                  label: of(this.getLevelLabel(app.scenarioDLevel)),
                  description: of(this.data.header.scenarioD),
                  value: app.scenarioDImplValue,
                  options: this.generateChartPieOption(app.scenarioDImplValue),
                },
              ]
            : undefined;
        const widgetRidValue: AppSummaryRowChart[] | undefined =
          widgetValue &&
          (widgetValue.scenarioRImplValue ||
            widgetValue.scenarioIImplValue ||
            widgetValue.scenarioDImplValue)
            ? [
                {
                  label: of(this.getLevelLabel(widgetValue.scenarioRLevel)),
                  description: of(this.data.header.scenarioR),
                  value: widgetValue.scenarioRImplValue,
                  options: this.generateChartPieOption(
                    widgetValue.scenarioRImplValue
                  ),
                },
                {
                  label: of(this.getLevelLabel(widgetValue.scenarioILevel)),
                  description: of(this.data.header.scenarioI),
                  value: widgetValue.scenarioIImplValue,
                  options: this.generateChartPieOption(
                    widgetValue.scenarioIImplValue
                  ),
                },
                {
                  label: of(this.getLevelLabel(widgetValue.scenarioDLevel)),
                  description: of(this.data.header.scenarioD),
                  value: widgetValue.scenarioDImplValue,
                  options: this.generateChartPieOption(
                    widgetValue.scenarioDImplValue
                  ),
                },
              ]
            : undefined;
        if (widgetValue) {
          const isDifferentValue = WidgetAppSummaryV0Component.isDifferentValue(
            app,
            widgetValue
          );
          const isRemoved = !app.treatmentSelected;

          switch (true) {
            case widgetValue.appAssessmentUnsupported &&
              app.appAssessmentUnsupported: {
              // widget.unsupported == app.unsupported == true
              // no diff, fai vedere "Non è necessario eseguire l'assessment"
              if (isRemoved) {
                const row: AppSummaryRowV2 = {
                  configuration: AppSummaryConfiguration.CASO_01,
                  status: 'REMOVED',
                  appId: app.id,
                  appUUID: app.uuid,
                  appName: app.name,
                  warningMessage: of(
                    'Applicativo non più disponibile nel trattamento.'
                  ),
                  showAlignBtn: false,
                  showRemoveBtn: true,
                };
                responseV2.push(row);
              } else {
                const row: AppSummaryRowV2 = {
                  configuration: AppSummaryConfiguration.CASO_02,
                  status: 'EQUAL',
                  appId: app.id,
                  appUUID: app.uuid,
                  appName: app.name,
                  appMessage: of("Non è necessario eseguire l'assessment"),
                  showAlignBtn: false,
                  showRemoveBtn: false,
                };
                responseV2.push(row);
              }
              break;
            }

            case !widgetValue.appAssessmentUnsupported &&
              !app.appAssessmentUnsupported: {
              // Verificare widget.rid == app.ridd
              if (isDifferentValue) {
                // Mostrare le differenze + allinea
                if (isRemoved) {
                  const row: AppSummaryRowV2 = {
                    configuration: AppSummaryConfiguration.CASO_04,
                    status: 'REMOVED', //'REMOVED',
                    appId: app.id,
                    appUUID: app.uuid,
                    appName: app.name,
                    showAlignBtn: false,
                    widgetRidValue,
                    widgetMessage: widgetRidValue
                      ? undefined
                      : of(
                          'Non sono disponibili valori di RID per questo applicativo'
                        ),
                    warningMessage: of(
                      'Applicativo non più disponibile nel trattamento.'
                    ),
                    showRemoveBtn: true,
                  };
                  responseV2.push(row);
                } else {
                  const row: AppSummaryRowV2 = {
                    configuration: AppSummaryConfiguration.CASO_03,
                    status: 'CHANGED',
                    appId: app.id,
                    appUUID: app.uuid,
                    appName: app.name,
                    showAlignBtn: true,
                    appRidValue,
                    appMessage: appRidValue
                      ? undefined
                      : of(
                          'Non sono disponibili valori di RID per questo applicativo'
                        ),
                    widgetRidValue,

                    widgetMessage: widgetRidValue
                      ? undefined
                      : of(
                          'Non sono disponibili valori di RID per questo applicativo'
                        ),
                    showRemoveBtn: false,
                  };
                  responseV2.push(row);
                }
              } else {
                if (isRemoved) {
                  const row: AppSummaryRowV2 = {
                    configuration: AppSummaryConfiguration.CASO_06,
                    status: 'REMOVED',
                    appId: app.id,
                    appUUID: app.uuid,
                    appName: app.name,
                    showAlignBtn: false,
                    warningMessage: of(
                      'Applicativo non più disponibile nel trattamento.'
                    ),
                    widgetMessage: widgetRidValue
                      ? undefined
                      : of(
                          'Non sono disponibili valori di RID per questo applicativo'
                        ),
                    widgetRidValue,
                    showRemoveBtn: true,
                  };
                  responseV2.push(row);
                } else {
                  const row: AppSummaryRowV2 = {
                    configuration: AppSummaryConfiguration.CASO_05,
                    status: 'EQUAL',
                    appId: app.id,
                    appUUID: app.uuid,
                    appName: app.name,
                    showAlignBtn: false,
                    widgetMessage: widgetRidValue
                      ? undefined
                      : of(
                          'Non sono disponibili valori di RID per questo applicativo'
                        ),
                    widgetRidValue,
                    showRemoveBtn: false,
                  };
                  responseV2.push(row);
                }
              }
              break;
            }

            case widgetValue.appAssessmentUnsupported &&
              !app.appAssessmentUnsupported: {
              // Widget Non è necessario eseguire l'assessmnet l'applicativo ha valore : ( Non disponibile || RID )
              if (isRemoved) {
                const row: AppSummaryRowV2 = {
                  configuration: AppSummaryConfiguration.CASO_08,
                  status: 'REMOVED',
                  appId: app.id,
                  appUUID: app.uuid,
                  appName: app.name,
                  showAlignBtn: false,
                  warningMessage: of(
                    'Applicativo non più disponibile nel trattamento.'
                  ),
                  widgetMessage: of("Non è necessario eseguire l'assessment"),
                  // appMessage: appRidValue ? undefined : of('Non disponibile'),
                  // appRidValue,
                  showRemoveBtn: true,
                };
                responseV2.push(row);
              } else {
                const row: AppSummaryRowV2 = {
                  configuration: AppSummaryConfiguration.CASO_07,
                  status: 'CHANGED',
                  appId: app.id,
                  appUUID: app.uuid,
                  appName: app.name,
                  showAlignBtn: true,
                  widgetMessage: of("Non è necessario eseguire l'assessment"),
                  appMessage: appRidValue ? undefined : of('Non disponibile'),
                  appRidValue,
                  showRemoveBtn: false,
                };
                responseV2.push(row);
              }

              break;
            }

            case !widgetValue.appAssessmentUnsupported &&
              app.appAssessmentUnsupported: {
              if (isRemoved) {
                const row: AppSummaryRowV2 = {
                  configuration: AppSummaryConfiguration.CASO_10,
                  status: 'REMOVED',
                  appId: app.id,
                  appUUID: app.uuid,
                  appName: app.name,
                  warningMessage: of(
                    'Applicativo non più disponibile nel trattamento.'
                  ),
                  showAlignBtn: false,
                  // appMessage: of("Non è necessario eseguire l'assessment"),
                  widgetMessage: widgetRidValue
                    ? undefined
                    : of('Non disponibile'),
                  widgetRidValue,
                  showRemoveBtn: true,
                };
                responseV2.push(row);
              } else {
                const row: AppSummaryRowV2 = {
                  configuration: AppSummaryConfiguration.CASO_09,
                  status: 'CHANGED',
                  appId: app.id,
                  appUUID: app.uuid,
                  appName: app.name,
                  showAlignBtn: true, // MOSTRO ALLINEA
                  appMessage: of("Non è necessario eseguire l'assessment"),
                  widgetMessage: widgetRidValue
                    ? undefined
                    : of('Non disponibile'),
                  widgetRidValue,
                  showRemoveBtn: false,
                };
                responseV2.push(row);
              }

              break;
            }
          }
        } else {
          if (app.treatmentSelected) {
            if (app.appAssessmentUnsupported) {
              const row: AppSummaryRowV2 = {
                configuration: AppSummaryConfiguration.CASO_11,
                status: 'ADDED',
                appId: app.id,
                appUUID: app.uuid,
                appName: app.name,
                widgetMessage: of(
                  'Nessun assessment nella valutazione del rischio'
                ),
                appMessage: of("Non è necessario eseguire l'assessment"),
                showAlignBtn: true,
                showRemoveBtn: false,
              };
              responseV2.push(row);
            } else {
              const row: AppSummaryRowV2 = {
                configuration: AppSummaryConfiguration.CASO_11,
                status: 'ADDED',
                appId: app.id,
                appUUID: app.uuid,
                appName: app.name,
                widgetMessage: of(
                  'Nessun assessment nella valutazione del rischio'
                ),
                appMessage: appRidValue ? undefined : of('Non disponibile'),
                appRidValue,
                showAlignBtn: true,
                showRemoveBtn: false,
              };
              responseV2.push(row);
            }
          }
        }
      });

      dataWidgetValue.forEach((widgetValue) => {
        const appValue: WidgetAppSummaryV0QueryResponseDto | undefined =
          dataQueryValues.find((r) => r.id === widgetValue.id);
        if (!appValue) {
          // QUESTO CASO NON PUO' SUCCEDERE
          console.error(
            'Widget  ' + widgetValue.name + ' non esiste più nel database'
          );
        }
      });
    }
    this.rowDataV2 = responseV2;
    this.noDataV2 = this.rowDataV2?.length === 0;
  }

  getLevelLabel(level: AppAssessmentLevel): string {
    return this.ridMapping?.find((d) => d.level === level)?.label || ND;
  }

  generateChartPieOption(value: number): EChartsOption {
    const currentMaxValue = 5;
    return {
      series: [
        {
          top: '0px',
          left: '0px',
          avoidLabelOverlap: false,
          type: 'pie',
          radius: ['65%', '55%'],
          center: ['50%', '50%'],
          animation: true,
          emphasis: {
            label: {
              show: false,
            },
          },
          label: {
            show: false,
          },
          labelLine: {
            show: false,
          },
          tooltip: {
            show: false,
          },
          data: [
            {
              value: currentMaxValue,
              itemStyle: { color: this.getColor(value) },
            },
          ],
        },
      ],
    } as EChartsOption;
  }

  public getColor(currentValue: number) {
    for (const c of RID_GAUGE_PALETTE) {
      if (currentValue <= c.maxValue) {
        return c.color;
      }
    }
    return '#333333';
  }
}
/*
const FAKE_WIDGET_VALUE: WidgetAppSummaryV0ResponseValue[] = [
  {
    id: 1,
    uuid: 'sar220506153935f_app',
    name: 'SARU Sist. Amm. Ris. Umane',
    assessmentCategory: 'PRIMATOO',
    missingRIDValues: true,
    appAssessmentUnsupported: true,
  },
  {
    id: 158,
    uuid: 'sar220506153935x_app',
    name: 'SARU Sist. Amm. Ris. Umane V2',
    assessmentCategory: 'PRIMATOO',
    missingRIDValues: true,
    appAssessmentUnsupported: true,
  },
  {
    id: 58,
    uuid: 'sof2204121507ook_app',
    name: 'Software refertazioni',
    assessmentCategory: 'SUPPLIER',
    missingRIDValues: false,
    appAssessmentUnsupported: false,
    scenarioRImplValue: 50,
    scenarioIImplValue: 30,
    scenarioDImplValue: 30,
    scenarioRLevel: AppAssessmentLevel.MEDIUM,
    scenarioILevel: AppAssessmentLevel.HIGH,
    scenarioDLevel: AppAssessmentLevel.HIGH,
  },
  {
    id: 88,
    uuid: 'ges2205061538kh3_app',
    name: 'Gestionale Risorse Umane v2',
    assessmentCategory: 'SUPPLIER',
    missingRIDValues: true,
    appAssessmentUnsupported: false,
  },
  {
    id: 57,
    uuid: 'sit2204082017cdf_app',
    name: 'Sito Web',
    assessmentCategory: 'INTERNAL',
    missingRIDValues: true,
    appAssessmentUnsupported: false,
  },
  {
    id: 582,
    uuid: 'sof2204121507ooF_app',
    name: 'Software refertazioni V2',
    assessmentCategory: 'SUPPLIER',
    missingRIDValues: false,
    appAssessmentUnsupported: false,
    scenarioRImplValue: 50,
    scenarioIImplValue: 30,
    scenarioDImplValue: 30,
    scenarioRLevel: AppAssessmentLevel.MEDIUM,
    scenarioILevel: AppAssessmentLevel.HIGH,
    scenarioDLevel: AppAssessmentLevel.HIGH,
  },
  {
    id: 55,
    uuid: 'off2204082012nfj_app',
    name: 'Office 365',
    assessmentCategory: null,
    missingRIDValues: false,
    appAssessmentUnsupported: true,
  },
  {
    id: 56,
    uuid: 'sui2204082014t8o_app',
    name: 'Suite Google (GMail, GDrive)',
    assessmentCategory: null,
    missingRIDValues: true,
    appAssessmentUnsupported: false,
  },

  {
    id: 60,
    uuid: 'ges2205061538kh2_app',
    name: 'Gestionale Risorse Umane',
    assessmentCategory: 'SUPPLIER',
    missingRIDValues: true,
    appAssessmentUnsupported: false,
  },
];

const FAKE_QUERY_VALUE: WidgetAppSummaryV0QueryResponseDto[] = [
  {
    id: 1,
    treatmentSelected: false,
    uuid: '01_app',
    missingRIDValues: true,
    assessmentCategory: 'PRIMATOO',
    name: 'SARU Sist. Amm. Ris. Umane',
    appAssessmentUnsupported: true,
  },
  {
    treatmentSelected: true,
    uuid: 'sar220506153935x_app',
    missingRIDValues: true,
    assessmentCategory: 'PRIMATOO',
    name: 'SARU Sist. Amm. Ris. Umane V2',
    appAssessmentUnsupported: true,
    id: 158,
  },
  {
    treatmentSelected: true,
    uuid: 'sof2204121507ook_app',
    missingRIDValues: false,
    assessmentCategory: 'SUPPLIER',
    name: 'Software refertazioni',
    appAssessmentUnsupported: false,
    id: 58,
    scenarioRImplValue: 30,
    scenarioIImplValue: 30,
    scenarioDImplValue: 30,
    scenarioRLevel: AppAssessmentLevel.HIGH,
    scenarioILevel: AppAssessmentLevel.HIGH,
    scenarioDLevel: AppAssessmentLevel.HIGH,
  },
  {
    treatmentSelected: false,
    uuid: 'ges2205061538kh3_app',
    missingRIDValues: true,
    assessmentCategory: 'SUPPLIER',

    name: 'Gestionale Risorse Umane v2',
    appAssessmentUnsupported: false,
    id: 88,

    scenarioRImplValue: 30,
    scenarioIImplValue: 30,
    scenarioDImplValue: 30,
    scenarioDLevel: AppAssessmentLevel.MEDIUM,
    scenarioILevel: AppAssessmentLevel.MEDIUM,
    scenarioRLevel: AppAssessmentLevel.MEDIUM,
  },
  {
    treatmentSelected: true,
    uuid: 'sit2204082017cdf_app',
    missingRIDValues: true,
    assessmentCategory: 'INTERNAL',
    name: 'Sito Web',
    appAssessmentUnsupported: false,
    id: 57,
    scenarioRImplValue: 30,
    scenarioIImplValue: 30,
    scenarioDImplValue: 30,
    scenarioDLevel: AppAssessmentLevel.MEDIUM,
    scenarioILevel: AppAssessmentLevel.MEDIUM,
    scenarioRLevel: AppAssessmentLevel.MEDIUM,
  },
  {
    treatmentSelected: false,
    uuid: 'sof2204121507ooF_app',
    missingRIDValues: false,
    assessmentCategory: 'SUPPLIER',
    name: 'Software refertazioni V2',
    appAssessmentUnsupported: false,
    id: 582,
    scenarioRImplValue: 30,
    scenarioIImplValue: 30,
    scenarioDImplValue: 30,
    scenarioRLevel: AppAssessmentLevel.HIGH,
    scenarioILevel: AppAssessmentLevel.HIGH,
    scenarioDLevel: AppAssessmentLevel.HIGH,
  },
  {
    scenarioRImplValue: null,
    scenarioIImplValue: null,
    treatmentSelected: true,
    uuid: 'off2204082012nfj_app',
    missingRIDValues: false,
    scenarioRLevel: null,
    assessmentCategory: null,
    scenarioDImplValue: null,
    name: 'Office 365',
    appAssessmentUnsupported: false,
    id: 55,
    scenarioDLevel: null,
    scenarioILevel: null,
  },
  {
    scenarioRImplValue: null,
    scenarioIImplValue: null,
    treatmentSelected: true,
    uuid: 'sui2204082014t8o_app',
    missingRIDValues: true,
    scenarioRLevel: null,
    assessmentCategory: null,
    scenarioDImplValue: null,
    name: 'Suite Google (GMail, GDrive)',
    appAssessmentUnsupported: true,
    id: 56,
    scenarioDLevel: null,
    scenarioILevel: null,
  },

  {
    treatmentSelected: false,
    uuid: 'ges2205061538kh2_app',
    missingRIDValues: true,
    assessmentCategory: 'SUPPLIER',

    name: 'Gestionale Risorse Umane',
    appAssessmentUnsupported: false,
    id: 60,

    scenarioRImplValue: null,
    scenarioIImplValue: null,
    scenarioDImplValue: null,
    scenarioRLevel: null,
    scenarioILevel: null,
    scenarioDLevel: null,
  },
];
*/

const FAKE_WIDGET_VALUE: WidgetAppSummaryV0ResponseValue[] = [
  {
    id: 1,
    name: 'App 01',
    uuid: '01_app',
    appAssessmentUnsupported: true,
    assessmentCategory: 'PRIMATOO',
    missingRIDValues: true,
  },
  {
    id: 2,
    name: 'App 02',
    uuid: '02_app',
    appAssessmentUnsupported: true,
    assessmentCategory: 'PRIMATOO',
    missingRIDValues: true,
  },
  {
    id: 3,
    name: 'App 03.0',
    uuid: '03.0_app',
    appAssessmentUnsupported: false,
    scenarioRImplValue: 50,
    scenarioIImplValue: 30,
    scenarioDImplValue: 30,
    scenarioRLevel: AppAssessmentLevel.MEDIUM,
    scenarioILevel: AppAssessmentLevel.HIGH,
    scenarioDLevel: AppAssessmentLevel.HIGH,
    assessmentCategory: 'SUPPLIER',
    missingRIDValues: false,
  },
  {
    id: 31,
    name: 'App 03.1',
    uuid: '03.1_app',
    appAssessmentUnsupported: false,
    assessmentCategory: 'INTERNAL',
    missingRIDValues: true,
  },
  {
    id: 5,
    name: 'App 05',
    uuid: '05_app',
    appAssessmentUnsupported: false,
    assessmentCategory: 'SUPPLIER',
    missingRIDValues: true,
  },

  {
    id: 6,
    name: 'App 06',
    uuid: '06_app',
    appAssessmentUnsupported: false,
    scenarioRImplValue: 50,
    scenarioIImplValue: 30,
    scenarioDImplValue: 30,
    scenarioRLevel: AppAssessmentLevel.MEDIUM,
    scenarioILevel: AppAssessmentLevel.HIGH,
    scenarioDLevel: AppAssessmentLevel.HIGH,
    assessmentCategory: 'SUPPLIER',
    missingRIDValues: false,
  },
  {
    id: 7,
    name: 'App 07',
    uuid: '07_app',
    appAssessmentUnsupported: false,
    assessmentCategory: null,
    missingRIDValues: true,
    scenarioRImplValue: 30,
    scenarioIImplValue: 30,
    scenarioDImplValue: 30,
    scenarioRLevel: AppAssessmentLevel.HIGH,
    scenarioILevel: AppAssessmentLevel.HIGH,
    scenarioDLevel: AppAssessmentLevel.HIGH,
  },
  {
    id: 8,
    name: 'App 08',
    uuid: '08_app',
    appAssessmentUnsupported: false,
    assessmentCategory: 'SUPPLIER',
    missingRIDValues: true,
  },
  {
    id: 9,
    name: 'App 09',
    uuid: '09_app',
    appAssessmentUnsupported: true,
    assessmentCategory: null,
    missingRIDValues: false,
  },
  {
    id: 10,
    name: 'App 10',
    uuid: '10_app',
    appAssessmentUnsupported: true,
    assessmentCategory: null,
    missingRIDValues: true,
  },
  {
    id: 11,
    name: 'App 11',
    uuid: '11_app',
    appAssessmentUnsupported: false,
    assessmentCategory: null,
    missingRIDValues: true,
  },
  {
    id: 12,
    name: 'App 12',
    uuid: '12_app',
    appAssessmentUnsupported: false,
    assessmentCategory: null,
    missingRIDValues: true,
  },
];
const FAKE_QUERY_VALUE: WidgetAppSummaryV0QueryResponseDto[] = [
  {
    id: 1,
    name: 'App 01',
    uuid: '01_app',
    appAssessmentUnsupported: true,
    treatmentSelected: false,
    assessmentCategory: 'PRIMATOO',
    missingRIDValues: true,
  },
  {
    id: 2,
    name: 'App 02',
    uuid: '02_app',
    appAssessmentUnsupported: true,
    treatmentSelected: true,
    assessmentCategory: 'PRIMATOO',
    missingRIDValues: true,
  },
  {
    id: 3,
    name: 'App 03',
    uuid: '03_app',
    appAssessmentUnsupported: false,
    treatmentSelected: true,
    scenarioRImplValue: 30,
    scenarioIImplValue: 30,
    scenarioDImplValue: 30,
    scenarioRLevel: AppAssessmentLevel.HIGH,
    scenarioILevel: AppAssessmentLevel.HIGH,
    scenarioDLevel: AppAssessmentLevel.HIGH,
    assessmentCategory: 'SUPPLIER',
    missingRIDValues: false,
  },
  {
    id: 4,
    name: 'App 04',
    uuid: '04_app',
    appAssessmentUnsupported: false,
    treatmentSelected: true,
    scenarioRImplValue: 30,
    scenarioIImplValue: 30,
    scenarioDImplValue: 30,
    scenarioRLevel: AppAssessmentLevel.MEDIUM,
    scenarioILevel: AppAssessmentLevel.MEDIUM,
    scenarioDLevel: AppAssessmentLevel.MEDIUM,
    assessmentCategory: 'INTERNAL',
    missingRIDValues: true,
  },
  {
    id: 5,
    name: 'App 05',
    uuid: '05_app',
    appAssessmentUnsupported: false,
    treatmentSelected: false,
    scenarioRImplValue: 30,
    scenarioIImplValue: 30,
    scenarioDImplValue: 30,
    scenarioRLevel: AppAssessmentLevel.MEDIUM,
    scenarioILevel: AppAssessmentLevel.MEDIUM,
    scenarioDLevel: AppAssessmentLevel.MEDIUM,
    assessmentCategory: 'SUPPLIER',
    missingRIDValues: true,
  },

  {
    id: 6,
    name: 'App 06',
    uuid: '06_app',
    appAssessmentUnsupported: false,
    treatmentSelected: false,
    scenarioRImplValue: 30,
    scenarioIImplValue: 30,
    scenarioDImplValue: 30,
    scenarioRLevel: AppAssessmentLevel.HIGH,
    scenarioILevel: AppAssessmentLevel.HIGH,
    scenarioDLevel: AppAssessmentLevel.HIGH,
    assessmentCategory: 'SUPPLIER',
    missingRIDValues: false,
  },
  {
    id: 7,
    name: 'App 07',
    uuid: '07_app',
    appAssessmentUnsupported: false,
    treatmentSelected: true,
    assessmentCategory: 'SUPPLIER',
    missingRIDValues: true,
    scenarioRImplValue: 30,
    scenarioIImplValue: 30,
    scenarioDImplValue: 30,
    scenarioRLevel: AppAssessmentLevel.HIGH,
    scenarioILevel: AppAssessmentLevel.HIGH,
    scenarioDLevel: AppAssessmentLevel.HIGH,
  },
  {
    id: 8,
    name: 'App 08',
    uuid: '08_app',
    appAssessmentUnsupported: false,
    treatmentSelected: false,
    assessmentCategory: 'SUPPLIER',
    missingRIDValues: true,
  },
  {
    id: 9,
    name: 'App 09',
    uuid: '09_app',
    appAssessmentUnsupported: false,
    treatmentSelected: true,
    assessmentCategory: null,
    missingRIDValues: false,
  },
  {
    id: 10,
    name: 'App 10',
    uuid: '10_app',
    appAssessmentUnsupported: false,
    treatmentSelected: false,
    assessmentCategory: null,
    missingRIDValues: true,
  },
  {
    id: 8,
    name: 'App 08',
    uuid: '08_app',
    appAssessmentUnsupported: true,
    treatmentSelected: true,
    assessmentCategory: null,
    missingRIDValues: true,
  },
  {
    id: 12,
    name: 'App 12',
    uuid: '12_app',
    appAssessmentUnsupported: true,
    treatmentSelected: false,
    assessmentCategory: null,
    missingRIDValues: true,
  },
  {
    id: 13,
    name: 'App 13',
    uuid: '13_app',
    appAssessmentUnsupported: true,
    treatmentSelected: true,
    assessmentCategory: null,
    missingRIDValues: true,
  },
  {
    id: 14,
    name: 'App 14',
    uuid: '14_app',
    appAssessmentUnsupported: false,
    treatmentSelected: true,
    assessmentCategory: null,
    missingRIDValues: true,
  },
  {
    id: 15,
    name: 'App 15',
    uuid: '15_app',
    appAssessmentUnsupported: false,
    treatmentSelected: true,
    assessmentCategory: null,
    missingRIDValues: true,
    scenarioRImplValue: 30,
    scenarioIImplValue: 30,
    scenarioDImplValue: 30,
    scenarioRLevel: AppAssessmentLevel.MEDIUM,
    scenarioILevel: AppAssessmentLevel.MEDIUM,
    scenarioDLevel: AppAssessmentLevel.MEDIUM,
  },
];
